import { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { ProfileModel } from '@entities/profile'
import { useSession, sessionRepository, useSetSession } from '@entities/session'
import { useSetProfile } from '@features/profile'
import { useLayoutEffect, useRef } from 'react'
import { REFRESH_PATH } from '@shared/api/server/auth'
import { authInstance, backendInstance } from '@shared/api/server/base'
import { useSetPrevLocation } from '@shared/lib/router'
import { useNavigate } from '@tanstack/react-router'

export const useInterceptors = () => {
  const navigate = useNavigate()
  const savePrevLocation = useSetPrevLocation()
  const { session } = useSession()
  const { setSession } = useSetSession()
  const { setProfile } = useSetProfile()
  const isRefreshing = useRef(false)
  const refreshSubscribers = useRef<(() => void)[]>([])

  const subscribeTokenRefresh = (cb: () => void) => {
    refreshSubscribers.current.push(cb)
  }

  const onRrefreshed = () => {
    refreshSubscribers.current.map((cb) => cb())
    refreshSubscribers.current = []
  }

  useLayoutEffect(() => {
    const backendRequestInterceptor = backendInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = session.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      async (error: AxiosError) => {
        await Promise.reject(error)
      }
    )

    const backendResponseInterceptor =
      backendInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalConfig = error.config
          if (error.response) {
            if (error.response.status === 401 && !originalConfig._retry) {
              if (isRefreshing.current) {
                return new Promise((resolve) => {
                  subscribeTokenRefresh(() => {
                    resolve(backendInstance(originalConfig))
                  })
                })
              }
              originalConfig._retry = true
              isRefreshing.current = true

              try {
                const result = await sessionRepository.update()
                isRefreshing.current = false
                onRrefreshed()
                setSession(result.session)
                setProfile(
                  new ProfileModel(
                    result.info.userFullName,
                    result.info.userRole,
                    result.info.paidUpUntil
                      ? new Date(result.info.paidUpUntil)
                      : new Date(),
                    result.info?.subDaysRemain ?? 0
                  )
                )
                return backendInstance(originalConfig)
              } catch {
                savePrevLocation(window.location.pathname)
                navigate({ to: '/signin' })
                throw error
              }
            }
            if (error.response.status === 403) {
              return navigate({ to: '/forbidden', replace: true })
            }
          }
          return Promise.reject(error?.response?.data?.detail[0])
        }
      )

    const authResponseInterceptor = authInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalConfig = error.config
        if (error.response) {
          if (
            error.response.status === 401 &&
            originalConfig.url === REFRESH_PATH
          ) {
            const path = window.location.pathname
            path !== `/signin` && savePrevLocation(window.location.pathname)
            navigate({ to: '/signin' })
            if (path !== `/signin`) {
              // return Promise.reject(error?.response?.data?.detail[0])
              return Promise.reject(null)
            }
          }
          if (error.response.status === 403) {
            return navigate({ to: '/forbidden', replace: true })
          }
        }
        return Promise.reject(error?.response?.data?.detail[0])
      }
    )

    return () => {
      backendInstance.interceptors.request.eject(backendRequestInterceptor)
      backendInstance.interceptors.response.eject(backendResponseInterceptor)
      authInstance.interceptors.response.eject(authResponseInterceptor)
    }
  }, [setSession, savePrevLocation, navigate, session, setProfile])

  return null
}
